/* global window */

import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'underscore';
import moment from 'moment';
import { Button, Input, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import * as videoActions from '../../../core/video/videoActions';
import GridView from '../../../components/elements/GridView';
import { secondsToHms } from '../../../core/utils/functions';
import VideoDownloadStatusModal from '../../../components/VideoDownloadStatusModal';
import { Edit, Time, Information } from '@carbon/icons-react';
import { getPaginationTotal } from '../../../components/elements/GridView/GridViewBase/Pagination';
import { getTodayEndInServerFormat } from '../../../core/utils/dateUtils';
import { UserRole } from '../../../types/types';

class CachedVideoTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            page: props?.pagination?.page || 1,
            search: {},
            order: {},
            selectedVideoId: null,
            videoFailed: false,
            selectedRequestVideoId: null,
            reference_text: null,
            modalSelectedReference: null,
        };
        props.registerCallback(this.filterDrivers);
    }

    async componentDidMount() {
        this.props.checkDvrStatus()
            .catch((error) => console.error(error));

        const {
            actions,
            assetReg,
            mapDate,
        } = this.props;
        const {
            page,
            search,
            order,
        } = this.state;
        let startdate = moment();

        const videoRetentionDays = this.props.videoRetentionDays;
        startdate = startdate.subtract(videoRetentionDays, 'days');
        startdate = startdate.format('YYYY-MM-DD');
        search.date_from = startdate;
        if (assetReg) {
            search.registration = assetReg;
            if (mapDate) {
                // If user comes from main map screen then use the map date to show events
                search.date_from = moment(mapDate)
                    .format('YYYY-MM-DD 00:00:00');
            }
        } else {
            search.date_to = getTodayEndInServerFormat();
        }

        actions.getRequestedVideosRequest({
            page,
            search,
            order,
        });
    }

    componentDidUpdate(prevProps) {
        const {
            forceDownloadFetching,
            actions,
        } = this.props;
        if (prevProps.forceDownloadFetching && !forceDownloadFetching) {
            const {
                page,
                search,
                order,
            } = this.state;
            actions.getRequestedVideosRequest({
                page,
                search,
                order,
            });
        }
    }

    showModal(ref) {
        this.setState({
            modalSelectedReference: ref,
        });
    }

    closeModal = () => {
        this.setState({
            modalSelectedReference: null,
        });
    };

    handleTableChange = (pagination, filters, sorter, extra) => {
        const { search } = this.state;
        const {
            actions,
            pagination: p,
        } = this.props;
        const { currentPage: page } = p;
        const order = [];
        // checking when only order changes
        if (pagination && pagination.current === page) {
            if (!isEmpty(sorter.order)) {
                order[sorter.field] = sorter.order ? sorter.order : 'ascend';
                this.setState({ order });
            }
            actions.getRequestedVideosRequest({
                page: 1,
                search,
                order,
            });
        }
    };

    editReference = (v) => {
        this.setState({
            selectedRequestVideoId: v.internal_reference,
            reference: v.reference,
        });
    };

    saveReference = (v) => {
        const { actions } = this.props;
        const {
            reference,
            page,
            search,
            order,
        } = this.state;

        actions.updateVideoReferenceRequest({
            id: v.internal_reference,
            reference,
            page,
            search,
            order,
        });
        this.setState({
            selectedRequestVideoId: null,
            reference: null,
        });
    };

    filterDrivers = (s) => {
        const { actions } = this.props;
        const { order } = this.state;
        const date_from = s.date_from && `${s.date_from.format('YYYY-MM-DD')} 00:00:00`;
        const date_to = s.date_to && `${s.date_to.format('YYYY-MM-DD')} 23:59:59`;

        const search = {
            ...s,
            date_from,
            date_to,
            date_range: false,
        };

        this.setState({ search });
        actions.getRequestedVideosRequest({
            page: 1,
            search,
            order,
        });
    };

    paginationChange = (page) => {
        const {
            actions,
            pagination,
        } = this.props;
        const { currentPage } = pagination;
        const {
            search,
            order,
        } = this.state;

        if (page !== currentPage) {
            this.setState({ page });
            actions.getRequestedVideosRequest({
                page,
                search,
                order,
            });
        }
    };

    /**
     * Converts seconds since midnight to a formatted MM:SS or HH:MM:SS string
     * @param {*} secondsSinceMidnight Number of seconds since midnight
     */
    convertFromSeconds(secondsSinceMidnight) {
        const t = secondsSinceMidnight;
        const hours = Math.floor(t / 3600);
        const minutes = `${Math.floor((t / 60) % 60)}`.padStart(2, '0');
        const seconds = `${Math.floor(t % 60)}`.padStart(2, '0');
        if (hours !== 0) {
            return `${hours}:${minutes}:${seconds}`;
        }
        return `${minutes}:${seconds}`;
    }

    handleRefreshClick = (v) => {
        const { actions } = this.props;
        const {
            asset_id,
            device_id,
            user_id,
            date,
            start_time,
            end_time,
        } = v;
        actions.postForceDownloadRequest({
            asset_id,
            device_id,
            user_id,
            date,
            start_time,
            end_time,
        });
    };

    showStatus = (v) => {
        const {
            status,
            online_status,
        } = v;
        let label = '';

        switch (status) {
            case 'queued':
            case 'selected':
                label = 'In Queue';
                break;
            case 'download-failed':
                label = 'Download failed';
                break;
            case 'device-offline':
                label = 'Device offline';
                break;
            case 'file-conversion-failed':
            case 'reconnecting':
            case 'partial-cached':
            case 'uploading':
            case 'pending-file-conversion':
            case 'file-conversion-in-progress':
            case 'downloading':
                label = 'Processing';
                break;
            case 'cached':
                label = 'Ready to view';
                break;
            case 'not-available':
                label = 'Not available';
                break;
            default:
                label = 'Discarded';
        }

        if (online_status != '1' && ['queued', 'device-offline', 'selected', 'download-failed', 'file-conversion-failed', 'reconnecting', 'partial-cached'].includes(status)) {
            label = 'Device Offline';
        }

        return (
            <div
                className="d-flex align-center"
            >
                <span className="ml-1">{label}</span>
                &ensp;&ensp;
                <Tooltip placement="top" title="Download Status">
                    <Button
                        className="btn btn-empty"
                        style={{ verticalAlign: 'middle' }}
                        onClick={e => {
                            e.stopPropagation();
                            this.showModal(v.internal_reference);
                        }}>
                        <Information />
                    </Button>
                </Tooltip>
            </div>
        );
    };

    handleExtendClick = (v) => {
        const { actions } = this.props;
        actions.extendStitchedVideoByIdRequest({ id: v.id });
    };

    render() {
        const {
            selectedRequestVideoId,
        } = this.state;
        const {
            videos,
            videos_loading,
            pagination,
            actions,
            user,
        } = this.props;

        const onlineIcon = (
            <svg height={12} width={12}>
                <circle cx={6} cy={6} r={3} fill="#2e9d0d" />
            </svg>
        );

        const offlineIcon = (
            <svg height={12} width={12}>
                <circle cx={6} cy={6} r={3} fill="#a4a4a4" />
            </svg>
        );

        const columns = (videos && videos.map((v) => {
            let daysRemaining = !Number(v.date_diff) ? '-' : `${v.date_diff} days`;
            if (v.date_diff && v.date_diff <= 0) {
                daysRemaining = (
                    <Button type="link" onClick={() => { this.handleExtendClick(v); }} style={{ padding: 0 }}>
                        Extend
                    </Button>
                );
            }

            const temp = {
                id: v.id,
                reference: (
                    selectedRequestVideoId != v.internal_reference ? (
                        <>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    this.editReference(v);
                                }}
                                style={{ display: 'inline-block' }}
                            >
                                {v.reference || '(Not Set)'}
                                &ensp;&ensp;
                                <Button className="btn btn-empty" style={{ verticalAlign: 'middle' }}>
                                    <Edit />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div onClick={e => {
                            e.stopPropagation();
                        }}>
                            <Input
                                defaultValue={v.reference}
                                size="small"
                                placeholder="Reference"
                                onChange={(e) => this.setState({ reference: e.target.value })}
                                maxLength={50}
                            />
                            <div>
                                &ensp;&ensp;
                                <Button
                                    className="btn btn-empty"
                                    onClick={e => {
                                        this.saveReference(v);
                                    }}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    )
                ),
                status: this.showStatus(v),
                asset: (
                    v.asset_id === null ? '-'
                        : v.asset_deleted === '1' ? (
                            <span>{v.online_status == '1' ? onlineIcon : offlineIcon} {v.asset_registration}</span>
                        ) : (
                            <div onClick={(e) => {
                                e.stopPropagation();
                            }}>
                                <Link to={`/assets/view/${v.asset_id}`} className="btn-link">
                                    {v.online_status == '1' ? onlineIcon : offlineIcon} {v.asset_registration}
                                </Link>
                            </div>
                        )
                ),
                driver: (
                    v.user_id === null ? '-'
                        : v.user_deleted === '1' ? v.user_name : (
                            <div onClick={(e) => {
                                e.stopPropagation();
                            }}>
                                <Link to={`/drivers/view/${v.user_id}`} className="btn-link">
                                    {v.user_name}
                                </Link>
                            </div>
                        )
                ),
                date: v.date && moment(v.date)
                    .format('DD/MM/YYYY'),
                start_time: secondsToHms(v.start_time, 'time'),
                end_time: secondsToHms(v.end_time, 'time'),
                location: v.location ? v.location : '-',
                duration: (
                    <div className="d-flex align-center">
                        <Time />
                        <span className="ml-1">{secondsToHms(v.duration, 'time')} </span>
                    </div>
                ),
                requested_time: v.requested_time && moment(v.requested_time)
                    .format('DD/MM/YYYY HH:mm:ss'),
                requested_by: v.requested_user_name,
                days_left: daysRemaining,
                actions: (
                    <Link
                        to={`/cached-video/view/${v.internal_reference}`}
                        className="btn-actions"
                    >
                        View
                    </Link>
                ),
            };
            if ([UserRole.SuperAdmin, UserRole.Admin, UserRole.SalesAdmin].includes(user.role)) {
                temp.admin_actions = (
                    <div style={{ minWidth: 100 }}>
                        <Popconfirm
                            title="Are you sure to delete this cached video?"
                            onConfirm={(e) => {
                                e.stopPropagation();
                                actions.deleteCachedVideoRequest({ internal_reference: v.internal_reference });
                            }}
                            onCancel={(e) => {
                                e.stopPropagation();
                            }}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Link className="btn-actions">
                                Delete
                            </Link>
                        </Popconfirm>
                    </div>
                );
            }
            return temp;
        })) || [];
        const { modalSelectedReference } = this.state;
        return (
            <>

                <GridView
                    data={columns}
                    onChange={this.handleTableChange}
                    sortableColumns={[
                        'reference',
                        'date',
                        'asset',
                        'requested_time',
                        'start_time',
                        'end_time',
                        'requested_by',
                        'days_left',
                        'driver',
                        'asset_reg',
                        'status',
                        'duration',
                        'channel',
                    ]}
                    hiddenColumns={['id']}
                    overrideColumnWidths={[{
                        key: 'reference',
                        width: 64,
                    }]}
                    pagination={{
                        total: parseInt(pagination.totalRecords, 10) || 0,
                        current: parseInt(pagination.currentPage, 10),
                        pageSize: parseInt(pagination.perPageCount, 10),
                        showSizeChanger: false,
                        onChange: this.paginationChange,
                        showTotal: getPaginationTotal,
                    }}
                />

                <VideoDownloadStatusModal
                    videoInternalReference={modalSelectedReference}
                    closeModal={this.closeModal.bind(this)}
                />
            </>
        );
    }
}

CachedVideoTab.defaultProps = {
    registerCallback: () => null,
    assetId: 0,
};

CachedVideoTab.propTypes = {
    actions: PropTypes.object.isRequired,
    videos: PropTypes.array.isRequired,
    video_url: PropTypes.object.isRequired,
    videos_loading: PropTypes.bool.isRequired,
    pagination: PropTypes.object.isRequired,
    singleVideo: PropTypes.object.isRequired,
    forceDownloadFetching: PropTypes.bool.isRequired,
    registerCallback: PropTypes.func,
    assetId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    assetReg: PropTypes.string,
    mapDate: PropTypes.string,
    user: PropTypes.object.isRequired,
    checkDvrStatus: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
        isFetching: state.video.isFetching,
        videos: state.video.requested,
        videos_loading: state.video.requestedVideosIsFetching,
        video_url: state.video.video_url,
        pagination: state.video.requestedPagination,
        singleVideo: state.video.singleVideo,
        forceDownloadFetching: state.video.forceDownloadFetching,
        assetReg: state.global.videoSearchFilters.assetReg,
        mapDate: state.global.mapDate,
        goPreviousPage: state.video.goPreviousPage,
        user: state.user,
        videoRetentionDays: state.user.userCompany.video_retention_days,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({
            ...videoActions,
        }, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(CachedVideoTab);
